import cx from 'classnames';
import { MDText } from 'i18n-react';
import { isUndefined } from 'lodash';

import cypressIds from 'Components/cypress_ids.json';
import SectionHeading from 'Components/Insights/components/SectionHeading/SectionHeading';
import { checkModelIsNotOther } from 'Components/Insights/hooks/useGetMakesAndModels.helpers';
import { useInsightsRouter } from 'Components/Insights/hooks/useInsightsRouter';
import {
	triggerInsightsRecentSoldPaginationEvent,
	triggerInsightsRecentSoldVehicleClickedEvent,
	triggerInsightsSortingEvent,
} from 'Services/analytics/events';
import Datagrid, { DatagridBody, DatagridCell, DatagridHeader, DatagridRow } from 'Storybook/core/Datagrid';
import type { ColumnCellProps } from 'Storybook/core/Datagrid/Datagrid.types';
import { CellTextAlign } from 'Storybook/core/Datagrid/Datagrid.types';
import Pagination from 'Storybook/core/Pagination/Pagination';
import type { Order } from 'Types/generic';
import { DATE_FORMATS, formatDate, formatText, gbpCurrencyFormatter } from 'Utilities/formatters';
import type { UseInsightsRecentSoldResult } from 'Utilities/hooks/recentSoldPrices/useRecentSoldPrices';
import ROUTES from 'Utilities/routes';

import { ITEMS_PER_PAGE, PLACEHOLDER } from '../RecentSoldPrices/RecentSoldPrices.consts';
import { numberFieldHasValue, stringFieldHasValue } from '../RecentSoldPrices/RecentSoldPrices.helpers';

import { filterRecentSoldPrices } from './RecentSoldPricesDataGrid.helpers';
import LocalTexts from './RecentSoldPricesDataGrid.texts';

import styles from './RecentSoldPricesDataGrid.module.scss';

const LocalT = new MDText(LocalTexts);

type RecentSoldPricesDataGridProps = {
	insightsRecentSold: UseInsightsRecentSoldResult;
	prettyMake: string;
	prettyModel: string;
};

export const RecentSoldPricesDataGrid: React.FC<RecentSoldPricesDataGridProps> = ({
	insightsRecentSold,
	prettyMake: make,
	prettyModel: model,
}) => {
	const { page, sortBy, sortOrder, updateResultsQueryParams } = useInsightsRouter();

	const columns: ColumnCellProps[] = [
		{
			cypressId: cypressIds.insights.recentSoldDatagridHeaders.vehicle,
			id: 'vehicle',
			sortable: true,
			text: LocalT.translate('columns.vehicle'),
		},
		{
			cypressId: cypressIds.insights.recentSoldDatagridHeaders.year,
			id: 'regYear',
			sortable: true,
			text: LocalT.translate('columns.regYear'),
			textAlign: CellTextAlign.Right,
		},
		{
			cypressId: cypressIds.insights.recentSoldDatagridHeaders.mileage,
			id: 'mileage',
			sortable: true,
			text: LocalT.translate('columns.mileage'),
			textAlign: CellTextAlign.Right,
		},
		{
			cypressId: cypressIds.insights.recentSoldDatagridHeaders.serviceHistory,
			id: 'serviceHistory',
			sortable: true,
			text: LocalT.translate('columns.serviceHistory'),
			textAlign: CellTextAlign.Right,
		},
		{
			cypressId: cypressIds.insights.recentSoldDatagridHeaders.soldDate,
			id: 'soldDate',
			sortable: true,
			text: LocalT.translate('columns.soldDate'),
			textAlign: CellTextAlign.Right,
		},
		{
			cypressId: cypressIds.insights.recentSoldDatagridHeaders.soldPrice,
			id: 'soldPrice',
			sortable: true,
			text: LocalT.translate('columns.soldPrice'),
			textAlign: CellTextAlign.Right,
		},
	].map((column) => {
		const { id } = column;

		if (id === sortBy) {
			return { ...column, sortDirection: sortOrder };
		}

		return column;
	});

	const { data, isSuccess, status } = insightsRecentSold;

	const totalVehicles = data?.vehicles.length ?? 0;
	const disablePagination = !isSuccess;

	const vehicles = filterRecentSoldPrices({
		filters: { page, sortBy, sortOrder },
		recentSoldPricesVehicles: data?.vehicles,
	});

	const isOtherModel = !checkModelIsNotOther(model);

	return (
		<section>
			<div className={styles.header}>
				<SectionHeading text={LocalT.translate('heading')} />

				<Pagination
					cypressId={cypressIds.insights.recentSoldPricesPagination}
					isDisabled={disablePagination}
					onClick={(pageNumber, direction) => {
						triggerInsightsRecentSoldPaginationEvent({ direction, page: pageNumber });
					}}
					page={page}
					queryStatus={status}
					recordsPerPage={ITEMS_PER_PAGE}
					totalRecords={totalVehicles}
				/>
			</div>

			<div className={styles.gridWrapper}>
				<Datagrid colCount={columns.values.length} rowCount={totalVehicles}>
					<DatagridHeader
						columns={columns}
						gridClass={styles.datagridHeader}
						onSortHandler={(id: string, newOrder: Order) => {
							// eslint-disable-next-line @typescript-eslint/no-floating-promises
							updateResultsQueryParams({
								newQuery: {
									sortBy: id,
									sortOrder: newOrder,
								},
							});
							triggerInsightsSortingEvent(`${id} ${newOrder}`);
						}}
					/>
					<DatagridBody>
						{!isUndefined(vehicles) &&
							vehicles.map((vehicle, rowIndex) => {
								return (
									<DatagridRow
										key={rowIndex}
										as={ROUTES.VEHICLE.as(vehicle.enquiryId)}
										cypressId={`${cypressIds.insights.recentSoldPricesItem}-${rowIndex}`}
										gridClass={styles.row}
										href={`${ROUTES.VEHICLE.href}?vehicleId=${vehicle.enquiryId}`}
										index={rowIndex}
										onClick={() => {
											triggerInsightsRecentSoldVehicleClickedEvent({ enquiryId: vehicle.enquiryId });
										}}
									>
										<DatagridCell index={0} rowIndex={rowIndex}>
											<div className={styles.makeAndModel}>
												{make} {isOtherModel ? null : model}
											</div>
											{stringFieldHasValue(vehicle.derivative) && (
												<div className={styles.derivative}>{vehicle.derivative}</div>
											)}
										</DatagridCell>
										<DatagridCell index={1} rowIndex={rowIndex}>
											<div className={styles.cellValue}>
												{!numberFieldHasValue(vehicle.manufactureYear) ? PLACEHOLDER : vehicle.manufactureYear}
											</div>
										</DatagridCell>
										<DatagridCell index={2} rowIndex={rowIndex}>
											<div className={styles.cellValue}>
												{!numberFieldHasValue(vehicle.mileage) ? PLACEHOLDER : formatText(vehicle.mileage)}
											</div>
										</DatagridCell>
										<DatagridCell index={3} rowIndex={rowIndex}>
											<div className={styles.cellValue}>
												{!stringFieldHasValue(vehicle.serviceHistory)
													? PLACEHOLDER
													: LocalT.translate(`serviceHistory.${vehicle.serviceHistory}`)}
											</div>
										</DatagridCell>
										<DatagridCell index={4} rowIndex={rowIndex}>
											<div className={cx(styles.cellValue, styles.soldDate)}>
												{!stringFieldHasValue(vehicle.soldDate)
													? PLACEHOLDER
													: formatDate(vehicle.soldDate, DATE_FORMATS.DAY_MONTH_YEAR)}
											</div>
										</DatagridCell>
										<DatagridCell index={5} rowIndex={rowIndex}>
											<div className={cx(styles.cellValue, styles.soldPrice)}>
												{!numberFieldHasValue(vehicle.soldPrice)
													? PLACEHOLDER
													: gbpCurrencyFormatter({ maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(
															vehicle.soldPrice ?? 0,
													  )}
											</div>
										</DatagridCell>
									</DatagridRow>
								);
							})}
					</DatagridBody>
				</Datagrid>
			</div>
		</section>
	);
};
